import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrescriptionCard = ({ prescription, onView, onEdit, onRenew, onCancel }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'fulfilled':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'cancelled':
        return 'bg-error/10 text-error border-error/20';
      case 'expired':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return { icon: 'AlertTriangle', color: 'text-error' };
      case 'high':
        return { icon: 'AlertCircle', color: 'text-warning' };
      default:
        return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const priorityInfo = getPriorityIcon(prescription?.priority);

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-clinical transition-clinical">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-medium text-foreground">{prescription?.patientName}</h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(prescription?.status)}`}>
              {prescription?.status}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">ID: {prescription?.id}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name={priorityInfo?.icon} size={16} className={priorityInfo?.color} />
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Created</p>
            <p className="text-sm font-medium text-foreground">{prescription?.createdDate}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Pill" size={16} className="text-primary" />
          <span className="font-medium text-foreground">{prescription?.medication}</span>
          <span className="text-sm text-muted-foreground">({prescription?.dosage})</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{prescription?.frequency}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Duration: {prescription?.duration}</span>
        </div>
      </div>
      {prescription?.pharmacy && (
        <div className="bg-muted rounded-md p-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{prescription?.pharmacy?.name}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{prescription?.pharmacy?.address}</p>
          {prescription?.fulfillmentStatus && (
            <div className="flex items-center space-x-1 mt-2">
              <div className={`w-2 h-2 rounded-full ${
                prescription?.fulfillmentStatus === 'ready' ? 'bg-success' :
                prescription?.fulfillmentStatus === 'processing' ? 'bg-warning' : 'bg-muted-foreground'
              }`} />
              <span className="text-xs text-muted-foreground capitalize">{prescription?.fulfillmentStatus}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {prescription?.isBlockchainVerified && (
            <div className="flex items-center space-x-1">
              <Icon name="Shield" size={14} className="text-success" />
              <span className="text-xs text-success">Verified</span>
            </div>
          )}
          {prescription?.isFHIRCompliant && (
            <div className="flex items-center space-x-1">
              <Icon name="FileCheck" size={14} className="text-primary" />
              <span className="text-xs text-primary">FHIR</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="sm" iconName="Eye" onClick={() => onView(prescription?.id)}>
            View
          </Button>
          {prescription?.status === 'active' && (
            <>
              <Button variant="ghost" size="sm" iconName="Edit" onClick={() => onEdit(prescription?.id)}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={() => onRenew(prescription?.id)}>
                Renew
              </Button>
              <Button variant="ghost" size="sm" iconName="X" onClick={() => onCancel(prescription?.id)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionCard;